import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';

import { PageContent } from '@/components/ui/features/hubs-conference/PageContent/PageContent';
import { SearchInput } from '@/components/ui/features/hubs-conference/SearchInput/SearchInput';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { AccessFrom } from '@/enums/AccessFrom';
import { LectureSort } from '@/enums/LectureSort';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { fetchOmnisearchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryOmnisearchLectureInfinite } from '@/queries/omnisearch-thin-lectures';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const NewLibraryPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Fragment>
      <NextSeo title="Hub Lectures" />
      <Hydrate fallback={fallback}>
        <Layout accessFrom={AccessFrom.Hubs} navigationVariant="default">
          <SearchInput />
          <PageContent />
        </Layout>
      </Hydrate>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });
  const { slug, query, sort, subject_subtree_of_id, conference_id, tag_id, year_held_at } = urlQueryStringValidate<{
    slug: string;
    query: string;
    sort: LectureSort;
    subject_subtree_of_id: string;
    conference_id: string;
    tag_id: string;
    year_held_at: string;
  }>(context.query, {
    slug: StringParam,
    query: StringParam,
    sort: StringParam,
    subject_subtree_of_id: StringParam,
    conference_id: StringParam,
    tag_id: StringParam,
    year_held_at: StringParam,
  });

  const { data } = await store.fetchResource(getHubBySlug(slug));
  const { data: subtreeIds } = await store.fetchResourceRelationship(getHubsSubtree(data.id));

  store.fetchResourcesInfinite(
    queryOmnisearchLectureInfinite({
      filters: {
        library: 'true',
        scope: 'published',
        query,
        subject_id: subtreeIds,
        subject_subtree_of_id,
        conference_id,
        tag_id,
        year_held_at,
      },
      sort: sort === LectureSort.Relevance && !query ? `-${LectureSort.HeldAt}` : sort,
      pageSize: 12,
    }),
    {
      sideload: fetchOmnisearchLectureAccessPermissionsSideload(store),
    }
  );

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default NewLibraryPage;
